import React, { useState } from 'react';
import { FaRoute } from "react-icons/fa";
import { useNavigate, useLocation } from 'react-router-dom';
import { getAuth } from 'firebase/auth';
import { useAuth } from '../../../context/AuthContext';
import { Swiper, SwiperSlide } from 'swiper/react';
import { FreeMode, Navigation, Keyboard, Mousewheel } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/free-mode';
import 'swiper/css/navigation';

import { ChevronDown, ChevronUp, Train, Bus, Info, ExternalLink, Map as MapIcon } from 'lucide-react';
import LoadingModal from '../../modal/LoadingModal';
import { fetchFareQuote, FallbackAvailableError } from '../../../services/flightBookingAPI';


import './FlightTrainResultCard.css';

const compareClasses = (c1, c2) => {
    if (!c1 || !c2) return false;
    const normalize = (s) => String(s).replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
    return normalize(c1) === normalize(c2);
};

const SeatAvailabilityGrid = ({ leg, refreshedInfo, refreshingIdx, handleRefresh }) => {
    if (!leg?.seat_classes || leg.seat_classes.length === 0) return null;

    return (
        <div className="seat-availability-swiper-container">
            <Swiper
                spaceBetween={12}
                slidesPerView="auto"
                freeMode={true}
                navigation={true}
                observer={true}
                observeParents={true}
                modules={[FreeMode, Navigation, Keyboard, Mousewheel]}
                keyboard={{ enabled: true }}
                mousewheel={{ forceToAxis: true }}
                className="seat-availability-swiper"
            >
                {leg.seat_classes.filter(cls => cls !== 'GN').map((cls, idx) => {
                    const uniqueKey = `${leg.number}_${idx}`;
                    const refreshed = refreshedInfo[uniqueKey];
                    let availInfo = refreshed ? refreshed.avl : (leg.seat_avail?.[cls] || null);
                    let cost = refreshed ? refreshed.fare : (leg.seat_costs?.[cls] || null);
                    // Fallback: Check seat_avail_ui or seat_avail.fare for matching class
                    if (!cost || cost === '--') {
                        if (leg.seat_avail_ui && compareClasses(leg.seat_avail_ui.seat_class, cls)) {
                            cost = leg.seat_avail_ui.fare;
                        } else if (leg.seat_avail && leg.seat_avail.fare && (compareClasses(leg.seat_class_ui, cls) || compareClasses(leg.seat_avail.seat_class, cls))) {
                            cost = leg.seat_avail.fare;
                        }
                    }
                    if (!availInfo || availInfo === 'N/A') {
                        if (leg.seat_avail_ui && compareClasses(leg.seat_avail_ui.seat_class, cls)) {
                            availInfo = leg.seat_avail_ui.avl;
                        }
                    }

                    // Determine explicit quota from various sources
                    let foundQuota = refreshed ? refreshed.quota : null;
                    if (!foundQuota) {
                        if (leg.seat_avail_ui && compareClasses(leg.seat_avail_ui.seat_class, cls)) {
                            foundQuota = leg.seat_avail_ui.quota;
                        } else if (leg.seat_avail && (compareClasses(leg.seat_class_ui, cls) || (leg.seat_avail.seat_class && compareClasses(leg.seat_avail.seat_class, cls)))) {
                            foundQuota = leg.seat_avail.quota;
                        }
                    }

                    // Determine display info for Class and Quota
                    let displayCls = cls;
                    let hasData = (cost && cost !== '--') || (availInfo && availInfo !== 'N/A' && availInfo !== null);
                    let displayQuota = hasData ? (foundQuota || leg.category || 'GN') : 'N/A';

                    // If quota is "Reserved", and cls looks like a quota code, swap them
                    const quotaCodes = ['GN', 'TQ', 'PT', 'SS', 'LD', 'DF', 'FT'];
                    if ((displayQuota.toLowerCase().includes('reserved') || displayQuota === 'Reserved') && quotaCodes.includes(displayCls.toUpperCase())) {
                        displayQuota = displayCls;
                        displayCls = leg.train_class || 'N/A';
                    }

                    // Final cleanup: remove "Reserved" related text
                    if (displayQuota.toLowerCase().includes('reserved')) {
                        displayQuota = 'GN';
                    }

                    const availLower = availInfo?.toLowerCase() || '';
                    const isAvbl = availLower.includes('avail') || availLower.includes('avl') || availLower.includes('avbl');
                    const isRac = availLower.includes('rac');
                    const isReserved = availLower.includes('reserved') || availLower.includes('resv');
                    const isNotAvail = availLower.includes('not') || availLower.includes('wl') || availLower.includes('regret');

                    // Prioritize is_avail flag only if the class matches or it's refreshed specifically for this class
                    const statusFromFlag = refreshed ? refreshed.is_avail : (
                        leg.seat_avail_ui && compareClasses(leg.seat_avail_ui.seat_class, cls)
                            ? leg.seat_avail_ui.is_avail
                            : null
                    );

                    let statusClass = 'unknown';
                    if (isNotAvail) {
                        statusClass = 'not-available';
                    } else if (statusFromFlag === true || isRac || isAvbl) {
                        statusClass = 'available';
                    } else if (isReserved) {
                        statusClass = 'reserved';
                    }

                    return (
                        <SwiperSlide key={idx} style={{ width: 'auto' }}>
                            <div className={`seat-class-box ${statusClass}`}>
                                <div className="seat-card-grid">
                                    <div className="seat-grid-top-left">{displayCls}</div>
                                    <div className="seat-grid-top-right">({displayQuota})</div>
                                    <div className="seat-grid-bottom-left">
                                        <span className={`seat-status-text-styled ${statusClass}`}>
                                            {availInfo || 'N/A'}
                                        </span>
                                    </div>
                                    <div className="seat-grid-bottom-right">
                                        {cost ? `₹${cost}` : '--'}
                                    </div>
                                </div>

                                <div
                                    className={`seat-refresh-layer ${refreshingIdx === uniqueKey ? 'refreshing' : ''}`}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleRefresh(leg, cls, idx);
                                    }}
                                >
                                    {refreshingIdx === uniqueKey ? (
                                        <svg width="22" height="22" viewBox="0 0 50 50" className="seat-loading-spinner">
                                            <circle cx="25" cy="25" r="20" stroke="#888" strokeWidth="4" fill="none" strokeDasharray="31.4 31.4" strokeLinecap="round">
                                                <animateTransform attributeName="transform" type="rotate" from="0 25 25" to="360 25 25" dur="1s" repeatCount="indefinite" />
                                            </circle>
                                        </svg>
                                    ) : (
                                        <svg width="18" height="18" viewBox="0 0 20 20" fill="none" className="seat-refresh-svg">
                                            <path d="M4 4v5h5" stroke="#888" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                            <path d="M2.5 10a7.5 7.5 0 1 1 2.2 5.3" stroke="#888" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                        </svg>
                                    )}
                                </div>
                            </div>
                        </SwiperSlide>
                    );
                })}
            </Swiper>
        </div>
    );
};


const CombinedIcons = ({ legs }) => {
    const truncate = (name, limit = 12) => {
        if (!name) return "";
        return name.length > limit ? `${name.substring(0, limit)}...` : name;
    };

    return (
        <div className="combined-icons">
            {legs.filter(leg => leg.type !== 'layover').map((leg, idx, filtered) => (
                <React.Fragment key={idx}>
                    <div className="icon-name-wrapper" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        {leg.type === 'flight' ? (
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M2 20H18" stroke="#6366F1" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
                                <path d="M3.82496 12.1661C3.54996 11.9661 3.29996 11.7161 2.99997 10.8411C2.9186 10.6241 2.61108 9.53619 2.34997 8.54109C2.12972 7.7017 1.93346 6.93555 2.02497 6.74109C2.09998 6.54109 2.19996 6.39109 2.52496 6.19109C2.72496 6.06802 3.74996 5.81609 3.94996 5.76609C4.14996 5.71609 4.42495 5.69109 4.64996 5.76609C5.07496 5.84109 5.94996 7.11609 6.17496 7.26609C6.27495 7.36609 6.59996 7.657 6.97496 7.69109C7.24996 7.71609 7.52497 7.64109 7.82498 7.51609C8.09997 7.40151 13.525 4.76609 14.025 4.54109C18.1 2.84109 21.06 5.63609 21.51 6.23609C21.975 6.81609 22.075 6.99109 21.95 7.49109C21.7884 8.01609 21.35 8.11609 21.1 8.19109C20.85 8.26609 17.4 9.19109 16.05 9.56609C15.7551 9.6621 15.6111 9.85492 15.575 9.89109C15.4 10.1411 14.605 11.8411 14.38 12.2161C14.225 12.6161 13.8 13.1161 13.25 13.3161C12.675 13.5161 11.675 13.7411 11.45 13.8161C11.225 13.8911 10.7 14.0411 10.525 13.9911C10.3 13.9411 10.085 13.7161 10.185 13.3661C10.285 13.0161 10.475 12.0411 10.5 11.8911C10.525 11.7411 10.775 11.1161 10.5 11.0911C10.45 11.0161 9.92497 11.2411 9.14997 11.4161C8.57419 11.5782 7.97119 11.7386 7.54996 11.8411C5.92496 12.3161 5.0449 12.4411 4.84996 12.4411C4.47496 12.4411 4.19996 12.3911 3.82496 12.1661Z" stroke="#6366F1" strokeWidth="2.2" />
                            </svg>
                        ) : leg.type === 'bus' ? (
                            <Bus size={24} color="#FF5B15" strokeWidth={2.5} />
                        ) : (
                            <Train size={24} color="#F97316" strokeWidth={2.5} />
                        )}
                        <span className="leg-name-header" style={{
                            fontSize: '16px',
                            fontWeight: '700',
                            color: '#0F172A',
                            fontFamily: 'Inter, sans-serif'
                        }}>
                            {truncate(leg.name)}
                        </span>
                    </div>
                    {idx < filtered.length - 1 && <ChevronDown size={18} className="icon-sep" />}
                </React.Fragment>
            ))}
        </div>
    );
};

const ReachIcon = () => (
    <div className="reach-icon-circle">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M7 7V3.09961C7 2.54732 7.44772 2.09961 8 2.09961C8.55228 2.09961 9 2.54732 9 3.09961V7L9.01465 7.2959C9.08279 7.98275 9.38657 8.62876 9.87891 9.12109C10.4415 9.68358 11.2044 10 12 10L12.2969 9.98535C12.9834 9.91711 13.6289 9.61319 14.1211 9.12109C14.6134 8.62876 14.9172 7.98275 14.9854 7.2959L15 7V3.09961C15 2.54732 15.4477 2.09961 16 2.09961C16.5523 2.09961 17 2.54732 17 3.09961V7L16.9941 7.24707C16.9329 8.48306 16.4143 9.65596 15.5352 10.5352C14.6561 11.4141 13.4837 11.9318 12.248 11.9932L12 12C10.674 12 9.40251 11.4727 8.46484 10.5352C7.58565 9.65596 7.06706 8.48306 7.00586 7.24707L7 7Z" fill="#FF670A" />
            <path d="M7.29273 13.2927C7.65885 12.9266 8.23785 12.904 8.63063 13.2244L8.7068 13.2927L9.7068 14.2927L9.77516 14.3689C10.0955 14.7617 10.0729 15.3407 9.7068 15.7068C9.34068 16.0729 8.76168 16.0955 8.36891 15.7752L8.29273 15.7068L7.29273 14.7068L7.22437 14.6306C6.90402 14.2379 6.92662 13.6589 7.29273 13.2927Z" fill="#FF670A" />
            <path d="M15.3691 13.2244C15.7618 12.904 16.3408 12.9266 16.707 13.2927C17.0731 13.6589 17.0957 14.2379 16.7753 14.6306L16.707 14.7068L15.707 15.7068C15.3164 16.0973 14.6834 16.0973 14.2929 15.7068C13.9024 15.3163 13.9024 14.6833 14.2929 14.2927L15.2929 13.2927L15.3691 13.2244Z" fill="#FF670A" />
            <path d="M18.9912 9.65332C18.9055 7.92303 18.181 6.28061 16.9502 5.0498C15.7194 3.819 14.077 3.09452 12.3467 3.00879L12 3C10.1435 3 8.36256 3.73705 7.0498 5.0498C5.73705 6.36256 5 8.14348 5 10V14C5 16.2477 6.75228 18 9 18H15C17.2477 18 19 16.2477 19 14V10L18.9912 9.65332ZM21 14C21 17.3523 18.3523 20 15 20H9C5.64772 20 3 17.3523 3 14V10C3 7.61305 3.94791 5.32357 5.63574 3.63574C7.32357 1.94791 9.61305 1 12 1C14.3869 1 16.6764 1.94791 18.3643 3.63574C20.0521 5.32357 21 7.61305 21 10V14Z" fill="#FF670A" />
            <path d="M7.16806 18.4454C7.47441 17.9859 8.09525 17.8617 8.55478 18.1681C9.01431 18.4744 9.13847 19.0952 8.83212 19.5548L6.83212 22.5548C6.52577 23.0143 5.90493 23.1385 5.4454 22.8321C4.98587 22.5258 4.86171 21.9049 5.16806 21.4454L7.16806 18.4454Z" fill="#FF670A" />
            <path d="M15.4456 18.1681C15.8765 17.8808 16.4495 17.972 16.7718 18.3634L16.8323 18.4455L18.8323 21.4455L18.8851 21.5333C19.1221 21.9813 18.9858 22.545 18.555 22.8322C18.124 23.1195 17.5511 23.0283 17.2288 22.6369L17.1683 22.5548L15.1683 19.5548L15.1155 19.4669C14.8785 19.019 15.0148 18.4553 15.4456 18.1681Z" fill="#FF670A" />
        </svg>
    </div>
);

const TrainIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M7 6.99805V3.09766C7 2.54537 7.44772 2.09766 8 2.09766C8.55228 2.09766 9 2.54537 9 3.09766V6.99805L9.01465 7.29395C9.08279 7.9808 9.38657 8.6268 9.87891 9.11914C10.4415 9.68163 11.2044 9.99805 12 9.99805L12.2969 9.9834C12.9834 9.91516 13.6289 9.61123 14.1211 9.11914C14.6134 8.6268 14.9172 7.9808 14.9854 7.29395L15 6.99805V3.09766C15 2.54537 15.4477 2.09766 16 2.09766C16.5523 2.09766 17 2.54537 17 3.09766V6.99805L16.9941 7.24512C16.9329 8.48111 16.4143 9.65401 15.5352 10.5332C14.6561 11.4121 13.4837 11.9299 12.248 11.9912L12 11.998C10.674 11.998 9.40251 11.4708 8.46484 10.5332C7.58565 9.65401 7.06706 8.48111 7.00586 7.24512L7 6.99805Z" fill="#FF670A" stroke="#FF670A" strokeWidth="0.5" />
        <path d="M7.29296 13.2927C7.65908 12.9266 8.23808 12.904 8.63085 13.2244L8.70703 13.2927L9.70703 14.2927L9.77539 14.3689C10.0957 14.7617 10.0731 15.3407 9.70703 15.7068C9.34091 16.0729 8.76191 16.0955 8.36914 15.7752L8.29296 15.7068L7.29296 14.7068L7.22460 14.6306C6.90425 14.2379 6.92685 13.6589 7.29296 13.2927Z" fill="#FF670A" stroke="#FF670A" strokeWidth="0.5" />
        <path d="M15.3691 13.2244C15.7619 12.904 16.3409 12.9266 16.707 13.2927C17.0731 13.6589 17.0957 14.2379 16.7754 14.6306L16.707 14.7068L15.707 15.7068C15.3165 16.0973 14.6835 16.0973 14.293 15.7068C13.9024 15.3163 13.9024 14.6833 14.293 14.2927L15.293 13.2927L15.3691 13.2244Z" fill="#FF670A" stroke="#FF670A" strokeWidth="0.5" />
        <path d="M18.9912 9.65332C18.9055 7.92303 18.181 6.28061 16.9502 5.0498C15.7194 3.819 14.077 3.09452 12.3467 3.00879L12 3C10.1435 3 8.36256 3.73705 7.0498 5.0498C5.73705 6.36256 5 8.14348 5 10V14C5 16.2477 6.75228 18 9 18H15C17.2477 18 19 16.2477 19 14V10L18.9912 9.65332ZM21 14C21 17.3523 18.3523 20 15 20H9C5.64772 20 3 17.3523 3 14V10C3 7.61305 3.94791 5.32357 5.63574 3.63574C7.32357 1.94791 9.61305 1 12 1C14.3869 1 16.6764 1.94791 18.3643 3.63574C20.0521 5.32357 21 7.61305 21 10V14Z" fill="#FF670A" stroke="#FF670A" strokeWidth="0.5" />
        <path d="M7.16797 18.4454C7.47432 17.9859 8.09516 17.8617 8.55469 18.1681C9.01421 18.4744 9.13838 19.0952 8.83203 19.5548L6.83203 22.5548C6.52568 23.0143 5.90484 23.1385 5.44531 22.8321C4.98578 22.5258 4.86161 21.9049 5.16797 21.4454L7.16797 18.4454Z" fill="#FF670A" stroke="#FF670A" strokeWidth="0.5" />
        <path d="M15.4456 18.1681C15.8765 17.8808 16.4495 17.972 16.7718 18.3634L16.8323 18.4455L18.8323 21.4455L18.8851 21.5333C19.1221 21.9813 18.9858 22.545 18.555 22.8322C18.124 23.1195 17.5511 23.0283 17.2288 22.6369L17.1683 22.5548L15.1683 19.5548L15.1155 19.4669C14.8785 19.019 15.0148 18.4553 15.4456 18.1681Z" fill="#FF670A" stroke="#FF670A" strokeWidth="0.5" />
    </svg>
);



const FlightTimelineIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M18.6435 2.04384C19.5186 1.77463 20.492 1.70219 21.29 2.03408L21.4473 2.10537L21.5849 2.18935C21.7152 2.28337 21.822 2.40752 21.8945 2.55263L21.9658 2.70986C22.2977 3.50792 22.2253 4.48128 21.956 5.35634C21.6576 6.32636 21.0711 7.3428 20.207 8.20693L17.0937 11.3192L18.7764 18.9852L18.7803 19.0038L18.8086 19.1796C18.9086 19.9885 18.558 20.6513 18.0185 21.1054L17.9004 21.2001C17.8303 21.2527 17.7532 21.2954 17.6719 21.328L17.1719 21.5282C16.3923 21.84 15.4504 21.7468 14.8213 21.0477L14.7002 20.9003C14.6882 20.8843 14.677 20.8671 14.666 20.8505L11.7187 16.3886L9.99999 17.5351V19.9999C9.99999 20.2651 9.89455 20.5194 9.70702 20.7069L8.70702 21.7069C8.49517 21.9188 8.19949 22.0245 7.90135 21.995C7.60326 21.9655 7.33412 21.8038 7.16796 21.5546L5.27831 18.7206L2.4453 16.8319C2.19605 16.6658 2.03442 16.3966 2.00487 16.0985C1.97535 15.8004 2.08111 15.5047 2.29296 15.2929L3.29296 14.2929L3.3662 14.2265C3.54415 14.0806 3.76789 13.9999 3.99999 13.9999H6.46483L7.61034 12.2802L3.1494 9.33388C3.13275 9.32288 3.11556 9.31168 3.09960 9.2997C2.26729 8.67522 2.13913 7.65937 2.47167 6.82802L2.54296 6.68544L2.84276 6.18544C3.24290 5.45247 4.00344 5.08940 4.82616 5.1913L4.99608 5.21962L5.01464 5.22353L12.6797 6.90517L15.793 3.79287L16.124 3.48134C16.9106 2.78638 17.7949 2.30496 18.6435 2.04384ZM20.1826 3.8163C19.9496 3.80362 19.6298 3.83337 19.2314 3.95595C18.5765 4.15751 17.8428 4.57112 17.207 5.20693L13.707 8.70693C13.4659 8.94804 13.1182 9.04957 12.7851 8.97646L4.60350 7.18056L4.57714 7.17763C4.57049 7.18984 4.56476 7.20282 4.55760 7.21474L4.31542 7.61806C4.30354 7.65896 4.30017 7.68488 4.30077 7.69814L9.55077 11.1659C9.77270 11.3125 9.92773 11.541 9.98046 11.8017C10.0332 12.0623 9.97954 12.3333 9.83202 12.5546L7.83202 15.5546C7.64655 15.8328 7.33434 15.9999 6.99999 15.9999H4.80272L6.55467 17.1679L6.63378 17.2265C6.71017 17.2891 6.77703 17.3627 6.83202 17.4452L7.99999 19.1972V16.9999C7.99999 16.6655 8.16710 16.3533 8.44530 16.1679L11.4453 14.1679L11.5303 14.1171C11.734 14.0087 11.9701 13.9733 12.1982 14.0194C12.4589 14.0722 12.6874 14.2272 12.834 14.4491L16.3008 19.6991C16.3179 19.7000 16.3601 19.6992 16.4287 19.6718L16.7715 19.5341C16.8051 19.4999 16.8186 19.4804 16.8213 19.4745C16.8231 19.4703 16.8245 19.4663 16.8252 19.4599C16.8259 19.4526 16.8265 19.4324 16.8193 19.3964V19.3954L15.0234 11.2147C14.9503 10.8817 15.0518 10.534 15.2930 10.2929L18.7930 6.79287C19.4288 6.15706 19.8424 5.42338 20.0439 4.76845C20.1667 4.36949 20.1954 4.04935 20.1826 3.8163Z" fill="#6366F1" stroke="#6366F1" strokeWidth="1.2" />
    </svg>
);

const LayoverTimelineIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M8 19C8 17.8954 7.10457 17 6 17C4.89543 17 4 17.8954 4 19C4 20.1046 4.89543 21 6 21C7.10457 21 8 20.1046 8 19ZM10 19C10 21.2091 8.20914 23 6 23C3.79086 23 2 21.2091 2 19C2 16.7909 3.79086 15 6 15C8.20914 15 10 16.7909 10 19Z" fill="#475569" />
        <path d="M20 15.5C20 14.837 19.7364 14.2013 19.2676 13.7324C18.8573 13.3222 18.3194 13.0695 17.7471 13.0127L17.5 13H6.5C5.30653 13 4.16227 12.5256 3.31836 11.6816C2.52708 10.8904 2.06093 9.83506 2.00586 8.72266L2 8.5C2 7.30653 2.47445 6.16227 3.31836 5.31836C4.16227 4.47445 5.30653 4 6.5 4H15C15.5523 4 16 4.44772 16 5C16 5.55228 15.5523 6 15 6H6.5C5.83696 6 5.20126 6.26358 4.73242 6.73242C4.26358 7.20126 4 7.83696 4 8.5L4.0127 8.74707C4.06952 9.31937 4.32219 9.85735 4.73242 10.2676C5.20126 10.7364 5.83696 11 6.5 11H17.5L17.7227 11.0059C18.8351 11.0609 19.8904 11.5271 20.6816 12.3184C21.5256 13.1623 22 14.3065 22 15.5C22 16.6935 21.5256 17.8377 20.6816 18.6816C19.8904 19.4729 18.8351 19.9391 17.7227 19.9941L17.5 20H9C8.44772 20 8 19.5523 8 19C8 18.4477 8.44772 18 9 18H17.5L17.7471 17.9873C18.3194 17.9305 18.8573 17.6778 19.2676 17.2676C19.7364 16.7987 20 16.163 20 15.5Z" fill="#475569" />
        <path d="M20 5C20 3.89543 19.1046 3 18 3C16.8954 3 16 3.89543 16 5C16 6.10457 16.8954 7 18 7C19.1046 7 20 6.10457 20 5ZM22 5C22 7.20914 20.2091 9 18 9C15.7909 9 14 7.20914 14 5C14 2.79086 15.7909 1 18 1C20.2091 1 22 2.79086 22 5Z" fill="#475569" />
    </svg>
);

const TrainTimelineIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M7 10L5 12V18L7 20" stroke="#475569" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M17 10L19 12V18L17 20" stroke="#475569" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        <rect x="7" y="5" width="10" height="12" rx="2" stroke="#475569" strokeWidth="1.5" />
        <path d="M7 13H17" stroke="#475569" strokeWidth="1.5" />
        <circle cx="9.5" cy="10" r="1" fill="#475569" />
        <circle cx="14.5" cy="10" r="1" fill="#475569" />
    </svg>
);

const FlightTrainResultCard = ({ card, onViewMap, onBookClick, onShowError }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const [refreshingIdx, setRefreshingIdx] = useState(null);
    const [refreshedInfo, setRefreshedInfo] = useState({});
    const [isBookingLoading, setIsBookingLoading] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();
    const { openLoginModal } = useAuth();

    const handleBookFlight = async (legOverride = null) => {
        // Get Firebase user token
        const auth = getAuth();
        const user = auth.currentUser;

        // Save source and query to localStorage for persistence across reloads/logouts
        localStorage.setItem("booking_source", "search");
        localStorage.setItem("booking_search_query", location.search);

        // Pre-check removed to allow API to handle auth (consistent with other cards)

        let userToken = null;

        if (onBookClick) onBookClick();

        // Token will be fetched inside the try block

        // Use override leg if provided, otherwise find first flight leg
        const flightLeg = legOverride || card.legs?.find(l => l.type === 'flight');

        const trace_id = flightLeg?.trace_id || card.trace_id;
        const result_idx = flightLeg?.result_idx || card.result_idx;
        const is_lcc = flightLeg?.is_lcc !== undefined ? flightLeg.is_lcc : card.is_lcc;
        const valid_till_ts = flightLeg?.valid_till_ts || card.valid_till_ts;

        setIsBookingLoading(true);
        try {
            let userToken = null;
            if (user) {
                userToken = await user.getIdToken();
            }

            // Call verify API first
            const fareQuoteResponse = await fetchFareQuote(trace_id, result_idx, userToken);

            if (onBookClick) onBookClick();

            const params = new URLSearchParams();
            if (trace_id) params.append("trace_id", trace_id);
            if (result_idx !== undefined && result_idx !== null)
                params.append("result_idx", result_idx);
            if (is_lcc !== undefined && is_lcc !== null)
                params.append("is_lcc", is_lcc);
            if (valid_till_ts !== undefined && valid_till_ts !== null)
                params.append("valid_till_ts", valid_till_ts);

            const url = `/book-flight?${params.toString()}`;

            navigate(url, {
                state: {
                    preFetchedQuote: fareQuoteResponse,
                    trace_id,
                    result_idx,
                    is_lcc,
                    valid_till_ts,
                    baggage: flightLeg?.baggage || card.baggage,
                    userToken,
                    source: "search",
                    searchQuery: location.search,
                    flightDetails: {
                        departure: card.origin,
                        arrival: card.destination,
                        duration: card.totalDuration,
                        stops: card.stops,
                        price: card.price,
                        currency: card.currency || '₹',
                        tag: card.tags?.[0] || '',
                        modes: ['flight'],
                    },
                },
            });
        } catch (error) {
            console.error("Booking failed:", error);
            setIsBookingLoading(false);

            // Handle unauthorized error - show login modal
            const isAuthError =
                error.status === 401 ||
                error.response?.status === 401 ||
                error.message?.toLowerCase().includes("unauthorized") ||
                error.message?.toLowerCase().includes("login required") ||
                error.message?.toLowerCase().includes("auth verifier");

            if (isAuthError) {
                openLoginModal(() => handleBookFlight(legOverride));
            } else if (error instanceof FallbackAvailableError) {
                if (onShowError) onShowError(error);
            } else {
                if (onShowError) onShowError({
                    message: error.message || "Failed to load booking details. Please try again.",
                    operationName: "Booking Error"
                });
            }
        }
    };

    const handleRefresh = async (leg, cls, idx) => {
        const uniqueKey = `${leg.number}_${idx}`;
        setRefreshingIdx(uniqueKey);
        try {
            const parseDate = (dStr) => {
                if (!dStr) return null;
                if (!isNaN(dStr) && typeof dStr === 'number') return new Date(dStr);
                if (typeof dStr === 'string' && /^\d+$/.test(dStr)) return new Date(Number(dStr));
                const parts = String(dStr).split(/[-/\s]/);
                if (parts.length >= 3) {
                    const p0 = parseInt(parts[0], 10), p1 = parseInt(parts[1], 10), p2 = parseInt(parts[2], 10);
                    if (p2 > 2000 && p1 >= 1 && p1 <= 12 && p0 >= 1 && p0 <= 31) return new Date(p2, p1 - 1, p0);
                    if (p0 > 2000 && p1 >= 1 && p1 <= 12 && p2 >= 1 && p2 <= 31) return new Date(p0, p1 - 1, p2);
                }
                const d = new Date(dStr);
                return (!isNaN(d.getTime()) && d.getFullYear() > 2000) ? d : null;
            };

            let dateObj = parseDate(leg.origin?.dep_dt) || parseDate(leg.origin?.date);
            const formattedDate = dateObj
                ? `${String(dateObj.getDate()).padStart(2, '0')}-${String(dateObj.getMonth() + 1).padStart(2, '0')}-${dateObj.getFullYear()}`
                : '';

            if (!formattedDate) {
                console.error('Refresh aborted: No valid date found in leg', leg);
                setRefreshingIdx(null);
                return;
            }

            const params = new URLSearchParams({
                train_num: leg.number || '',
                src: leg.origin.code || '',
                dest: leg.destination.code || '',
                date: formattedDate,
                pref_train_class: cls,
                quota: (leg.category && leg.category !== 'Reserved' && leg.category !== '(Reserved)') ? leg.category : 'GN',
                api_key: 'web_dev_1',
                get_avail_only: '1'
            }).toString();

            const url = `https://hh183pxn0f.execute-api.ap-south-1.amazonaws.com/development/search?${params}`;
            const res = await fetch(url, { method: "GET" });
            const data = await res.json();

            if (data.status === 200 && data.data?.seat_avail_ui) {
                setRefreshedInfo(prev => ({
                    ...prev,
                    [uniqueKey]: {
                        avl: data.data.seat_avail_ui.avl,
                        fare: data.data.seat_avail_ui.fare,
                        is_avail: data.data.seat_avail_ui.is_avail,
                        quota: data.data.seat_avail_ui.quota
                    }
                }));
            }
        } catch (e) {
            console.error('Refresh error:', e);
        } finally {
            setTimeout(() => setRefreshingIdx(null), 500);
        }
    };



    const formatPrice = (amount) => {
        if (amount === null || amount === undefined) return null;
        const val = parseFloat(typeof amount === 'string' ? amount.replace(/,/g, '') : amount);
        if (isNaN(val) || !amount) return null;
        return val.toLocaleString('en-IN', {
            style: 'currency',
            currency: 'INR',
            maximumFractionDigits: 0
        });
    };

    const getLegDisplayPrice = (leg) => {
        if (!leg || leg.type === 'layover') return null;
        const legRefreshedKeys = Object.keys(refreshedInfo).filter(k => k.startsWith(`${leg.number}_`));
        if (legRefreshedKeys.length > 0) {
            const latestKey = legRefreshedKeys[legRefreshedKeys.length - 1];
            const info = refreshedInfo[latestKey];
            if (info && info.fare) return formatPrice(info.fare);
        }
        return leg.price || '-';
    };

    const displayPrice = (() => {
        let total = 0;
        let foundAny = false;

        card.legs?.forEach((leg) => {
            if (leg.type === 'layover') return;

            const legRefreshedKeys = Object.keys(refreshedInfo).filter(k => k.startsWith(`${leg.number}_`));
            if (legRefreshedKeys.length > 0) {
                const latestKey = legRefreshedKeys[legRefreshedKeys.length - 1];
                const info = refreshedInfo[latestKey];
                if (info && info.fare) {
                    total += parseFloat(typeof info.fare === 'string' ? info.fare.replace(/,/g, '') : info.fare);
                    foundAny = true;
                    return;
                }
            }

            if (leg.fare) {
                total += parseFloat(typeof leg.fare === 'string' ? leg.fare.replace(/,/g, '') : leg.fare);
                foundAny = true;
            }
        });

        if (foundAny) return formatPrice(total);
        return card.price || '-';
    })();

    return (
        <div className={`flight-train-card-container ${isExpanded ? 'expanded' : ''}`}>
            {/* Main Collapsed Card */}
            <div className="main-card-view" onClick={() => setIsExpanded(!isExpanded)}>
                <div className="card-header">
                    <div className="header-left">
                        <CombinedIcons legs={card.legs} />
                        {/* <span className="route-info-text">{card.routeInfo}</span> */}
                    </div>
                    <div className="card-tags">
                        <button 
                            className="result-card-map-btn icon-only"
                            onClick={(e) => {
                                e.stopPropagation();
                                onViewMap();
                            }}
                            title="View Route on Map"
                        >
                            <FaRoute size={16} />
                        </button>
                        {card.tags && card.tags.length > 0 && card.tags.map((tag, idx) => (
                            <span key={idx} className={`card-tag ${tag.toLowerCase()}`}>
                                {tag}
                            </span>
                        ))}
                    </div>
                </div>

                <div className="card-body">
                    <div className="journey-summary">
                        <div className="station-info">
                            <span className="station-name-label">{card.origin.name}</span>
                            <span className="station-code-time">{card.origin.code}, {card.origin.time}</span>
                            <span className="journey-date">{card.origin.date}</span>
                        </div>

                        <div className="visual-connector">
                            <div className="connector-top">
                                <div className="connector-line"></div>
                                <span className="duration-text">{card.totalDuration}</span>
                            </div>
                            <span className="halts-text">
                                {(card.halts || 0) === 0
                                    ? '0 change stop'
                                    : `${card.halts} change ${card.halts === 1 ? 'stop' : 'stops'}`
                                }

                            </span>
                        </div>

                        <div className="station-info right">
                            <span className="station-name-label">{card.destination.name}</span>
                            <span className="station-code-time">{card.destination.code}, {card.destination.time}</span>
                            <span className="journey-date">{card.destination.date}</span>
                        </div>
                    </div>

                    <div className="bottom-row">
                        <div className="left-footer-content" style={{ display: 'flex', alignItems: 'center', gap: '12px', minWidth: 0 }}>
                            {card.seatStatus ? (
                                <div className="seat-status-container" style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px'
                                }}>
                                    <div className="tooltip-container">
                                        <div className="tooltip-trigger">
                                            <Info size={18} color={
                                                card.seatStatus === "Seats available" ? "#16A34A" :
                                                    card.seatStatus === "Seats partially available" ? "#CA8A04" : "#DC2626"
                                            } />
                                            <span className="tooltip-text">
                                                {card.seatStatus === "Seats available"
                                                    ? "Seats available on all segments of this route"
                                                    : card.seatStatus === "Seats partially available"
                                                        ? "Seats are available on some segments of this route"
                                                        : "Seats are not available on this route"
                                                }
                                            </span>
                                        </div>
                                    </div>

                                    <span className="seat-status-text" style={{
                                        fontSize: '14px',
                                        fontWeight: '600',
                                        color: card.seatStatus === "Seats available" ? "#16A34A" :
                                            card.seatStatus === "Seats partially available" ? "#CA8A04" : "#DC2626",
                                        fontFamily: "'Inter', sans-serif"
                                    }}>
                                        {card.seatStatus}
                                    </span>
                                </div>
                            ) : (
                                card.fareOptions && card.fareOptions.length > 0 && (
                                    <div className="fare-pills-swiper-container">
                                        <Swiper
                                            spaceBetween={8}
                                            slidesPerView="auto"
                                            freeMode={true}
                                            navigation={true}
                                            observer={true}
                                            observeParents={true}
                                            modules={[FreeMode, Navigation, Keyboard, Mousewheel]}
                                            keyboard={{ enabled: true }}
                                            mousewheel={{ forceToAxis: true }}
                                            className="fare-pills-swiper"
                                        >
                                            {card.fareOptions.map((option, idx) => (
                                                <SwiperSlide key={idx} style={{ width: 'auto' }}>
                                                    <div className="standard-fare-pill">
                                                        <div className="pill-row-top">
                                                            <span className="class-code">{option.class}</span>
                                                            <span className="avail-status">{option.avail}</span>
                                                        </div>
                                                        <div className="pill-row-bottom">
                                                            <span className="total-price-label">Total price</span>
                                                            <span className="fare-amount">{option.price}</span>
                                                        </div>
                                                    </div>
                                                </SwiperSlide>
                                            ))}
                                        </Swiper>
                                    </div>
                                )
                            )}
                        </div>


                        <div className="action-column">
                            {(() => {
                                const activeLegs = card.legs.filter(l => l.type !== 'layover');
                                const allTrains = activeLegs.every(l => l.type === 'train');

                                if (allTrains) {
                                    const label = activeLegs.length === 1 ? 'Book Train (IRCTC)' : 'Book Trains (IRCTC)';
                                    return (
                                        <button
                                            className="book-now-btn train"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                const firstTrainLeg = activeLegs.find(l => l.booking_link);
                                                if (firstTrainLeg?.booking_link) {
                                                    window.open(firstTrainLeg.booking_link, '_blank', 'noopener,noreferrer');
                                                }
                                            }}
                                        >
                                            {label} <ExternalLink size={14} style={{ marginLeft: '6px' }} />
                                        </button>
                                    );
                                }

                                // For mixed or other multi-leg, show individual buttons in sequence
                                return activeLegs.map((leg, idx) => {
                                    const originText = leg.origin.code || leg.origin.name;
                                    const destText = leg.destination.code || leg.destination.name;
                                    const modeLabel = leg.type.charAt(0).toUpperCase() + leg.type.slice(1);

                                    return (
                                        <button
                                            key={idx}
                                            className={`book-now-btn ${leg.type}`}
                                            disabled={isBookingLoading}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                if (leg.type === 'flight') {
                                                    handleBookFlight(leg);
                                                } else if (leg.booking_link) {
                                                    window.open(leg.booking_link, '_blank', 'noopener,noreferrer');
                                                }
                                            }}
                                        >
                                            {isBookingLoading && leg.type === 'flight' ? "Processing..." : (
                                                <>
                                                    {leg.type === 'train'
                                                        ? `Book ${originText} → ${destText} Train (IRCTC)`
                                                        : `Book ${originText} → ${destText} ${modeLabel}`
                                                    }
                                                    {leg.type === 'train' && <ExternalLink size={14} style={{ marginLeft: '6px' }} />}
                                                </>
                                            )}
                                        </button>
                                    );
                                });

                            })()}

                            <div className="price-expand-row">
                                <span className="total-bold-price">{displayPrice}</span>
                                <button
                                    className="expand-trigger"
                                    onClick={() => setIsExpanded(!isExpanded)}
                                >
                                    {isExpanded ? <ChevronUp size={24} /> : <ChevronDown size={24} />}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Expanded Content */}
            {isExpanded && (
                <div className="expanded-timeline-view">
                    <div className="timeline-wrapper">
                        {(card.halts || 0) === 0 ? (
                            <div className="timeline-segment">
                                <div className="sidebar-visual">
                                    <div className="node-icon-box">
                                        {card.legs[0]?.type === 'flight' && <img src="/icon4.svg" alt="Flight" style={{ width: 24, height: 24 }} />}
                                        {card.legs[0]?.type === 'train' && <img src="/icon1.svg" alt="Train" style={{ width: 24, height: 24 }} />}
                                        {card.legs[0]?.type === 'bus' && <Bus size={20} color="#FF5B15" />}
                                    </div>
                                </div>
                                <div className="leg-details-container" style={{ flex: 1 }}>
                                    <div className={`expanded-leg-card ${card.legs[0]?.type}-type`}>
                                        <div className="leg-upper">
                                            <span className="leg-name-title">
                                                {card.legs[0]?.number}{card.legs[0]?.name ? `, ${card.legs[0].name}` : ''}
                                            </span>
                                            <span className="leg-type-tag">
                                                {card.legs[0]?.type?.charAt(0).toUpperCase() + card.legs[0]?.type?.slice(1)}
                                            </span>
                                        </div>

                                        <div className="leg-middle">
                                            <div className="leg-stations">
                                                <div className="leg-station">
                                                    <span className="s-sub">{card.legs[0]?.origin?.name}</span>
                                                    <span className="s-main">{card.legs[0]?.origin?.code}, {card.legs[0]?.origin?.time}</span>
                                                    <span className="s-date">{card.legs[0]?.origin?.date}</span>
                                                </div>
                                                <div className="leg-visual">
                                                    <span className="d-text">{card.legs[0]?.duration}</span>
                                                    <div className="d-line"></div>
                                                </div>
                                                <div className="leg-station right">
                                                    <span className="s-sub">{card.legs[0]?.destination?.name}</span>
                                                    <span className="s-main">{card.legs[0]?.destination?.code}, {card.legs[0]?.destination?.time}</span>
                                                    <span className="s-date">{card.legs[0]?.destination?.date}</span>
                                                </div>
                                            </div>
                                            {card.legs[0]?.type === 'train' && (
                                                <SeatAvailabilityGrid
                                                    leg={card.legs[0]}
                                                    refreshedInfo={refreshedInfo}
                                                    refreshingIdx={refreshingIdx}
                                                    handleRefresh={handleRefresh}
                                                />
                                            )}
                                        </div>
                                        <div className="leg-lower">
                                            <div className="leg-price-container">
                                                {card.legs[0]?.type === 'flight' && card.legs[0]?.price && (
                                                    <span className="leg-price-val">{card.legs[0].price}</span>
                                                )}
                                            </div>
                                            <button
                                                className={`book-leg-btn ${card.legs[0]?.type}`}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    if (card.legs[0]?.type === 'flight') {
                                                        handleBookFlight(card.legs[0]);
                                                    } else if (card.legs[0]?.booking_link) {
                                                        window.open(card.legs[0].booking_link, '_blank', 'noopener,noreferrer');
                                                    }
                                                }}
                                            >
                                                {card.legs[0]?.type === 'train' ? 'Book on IRCTC' : `Book ${card.legs[0].type.charAt(0).toUpperCase() + card.legs[0].type.slice(1)}`}
                                            </button>
                                        </div>

                                        {(card.legs[0]?.footerText || card.explanation) && (
                                            <div className="leg-footer-msg">
                                                {[
                                                    ...(card.explanation ? [card.explanation] : []),
                                                    ...(card.legs[0]?.footerText ? card.legs[0].footerText.split('\n') : [])
                                                ].filter(line => line.trim() !== '').map((line, i) => <p key={i}>{line}</p>)}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <>
                                {/* First leg commute card — rendered BEFORE the static start block when present */}
                                {card.legs[0]?.originCommentWeb && (
                                    <div className="timeline-segment">
                                        <div className="sidebar-visual">
                                            <div className="node-icon-box">
                                                <img src="/icon3.svg" alt="Commute" style={{ width: 24, height: 24 }} />
                                            </div>
                                            <div className="timeline-dashed-line"></div>
                                        </div>
                                        <div className="leg-details-container" style={{ paddingBottom: '24px' }}>
                                            <div className="expanded-leg-card location-type commute-type">
                                                <div className="leg-upper">
                                                    <span className="leg-name-title">{card.legs[0].originCommentWebTitle || 'Commute'}</span>
                                                    <span className="leg-type-tag">Commute</span>
                                                </div>
                                                <div className="leg-middle">
                                                    <p className="location-desc-text">{card.legs[0].originCommentWeb}</p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {card.legs.map((leg, index) => (
                                    <React.Fragment key={index}>
                                        {/* Commute card for legs after the first — shown inline before the segment */}
                                        {index > 0 && leg.originCommentWeb && (
                                            <div className="timeline-segment">
                                                <div className="sidebar-visual">
                                                    <div className="node-icon-box">
                                                        <img src="/icon6.svg" alt="Commute" style={{ width: 24, height: 24 }} />
                                                    </div>
                                                    <div className="timeline-dashed-line"></div>
                                                </div>
                                                <div className="leg-details-container" style={{ paddingBottom: '24px' }}>
                                                    <div className="expanded-leg-card location-type commute-type">
                                                        <div className="leg-upper">
                                                            <span className="leg-name-title">{leg.originCommentWebTitle || 'Commute'}</span>
                                                            <span className="leg-type-tag">Commute</span>
                                                        </div>
                                                        <div className="leg-middle">
                                                            <p className="location-desc-text">{leg.originCommentWeb}</p>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        )}

                                        <div className="timeline-segment">
                                            <div className="sidebar-visual">
                                                <div className="node-icon-box">
                                                    {leg.type === 'flight' && <img src="/icon4.svg" alt="Flight" style={{ width: 24, height: 24 }} />}
                                                    {leg.type === 'train' && <img src="/icon1.svg" alt="Train" style={{ width: 24, height: 24 }} />}
                                                    {leg.type === 'layover' && <img src="/icon6.svg" alt="Layover" style={{ width: 32, height: 32 }} />}
                                                    {leg.type === 'destination' && <ReachIcon />}
                                                </div>
                                                {(index < card.legs.length - 1 || card.destCommentWeb) && <div className="timeline-dashed-line"></div>}
                                            </div>

                                            <div className="leg-details-container" style={{ paddingBottom: (index < card.legs.length - 1 || card.destCommentWeb) ? '24px' : 0 }}>
                                                {(leg.type === 'flight' || leg.type === 'train') ? (
                                                    <div className={`expanded-leg-card ${leg.type}-type`}>
                                                        <div className="leg-upper">
                                                            <span className="leg-name-title">{leg.number}{leg.name ? `, ${leg.name}` : ''}</span>
                                                            <span className="leg-type-tag">{leg.type.charAt(0).toUpperCase() + leg.type.slice(1)}</span>
                                                        </div>

                                                        <div className="leg-middle">
                                                            <div className="leg-stations">
                                                                <div className="leg-station">
                                                                    <span className="s-sub">{leg.origin.name}</span>
                                                                    <span className="s-main">{leg.origin.code}, {leg.origin.time}</span>
                                                                    <span className="s-date">{leg.origin.date}</span>
                                                                </div>
                                                                <div className="leg-visual">
                                                                    <span className="d-text">{leg.duration}</span>
                                                                    <div className="d-line"></div>
                                                                </div>
                                                                <div className="leg-station right">
                                                                    <span className="s-sub">{leg.destination.name}</span>
                                                                    <span className="s-main">{leg.destination.code}, {leg.destination.time}</span>
                                                                    <span className="s-date">{leg.destination.date}</span>
                                                                </div>
                                                            </div>
                                                            {leg.type === 'train' && (
                                                                <SeatAvailabilityGrid
                                                                    leg={leg}
                                                                    refreshedInfo={refreshedInfo}
                                                                    refreshingIdx={refreshingIdx}
                                                                    handleRefresh={handleRefresh}
                                                                />
                                                            )}

                                                        </div>
                                                        <div className="leg-lower">
                                                            <div className="leg-price-container">
                                                                {leg.type === 'flight' && leg.price && (
                                                                    <span className="leg-price-val">{leg.price}</span>
                                                                )}
                                                            </div>
                                                            <button
                                                                className={`book-leg-btn ${leg.type}`}
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    if (leg.type === 'flight') {
                                                                        handleBookFlight(leg);
                                                                    } else if (leg.booking_link) {
                                                                        window.open(leg.booking_link, '_blank', 'noopener,noreferrer');
                                                                    }
                                                                }}
                                                            >
                                                                {leg.type === 'train' ? 'Book on IRCTC' : `Book ${leg.type.charAt(0).toUpperCase() + leg.type.slice(1)}`}
                                                            </button>
                                                        </div>
                                                        {(leg.footerText || (index === card.legs.length - 1 && card.explanation)) && (
                                                            <div className="leg-footer-msg">
                                                                {[
                                                                    ...(index === card.legs.length - 1 && card.explanation ? [card.explanation] : []),
                                                                    ...(leg.footerText ? leg.footerText.split('\n') : [])
                                                                ].filter(line => line.trim() !== '').map((line, i) => <p key={i}>{line}</p>)}
                                                            </div>
                                                        )}
                                                    </div>
                                                ) : (
                                                    <div className="expanded-leg-card location-type">
                                                        <div className="leg-upper">
                                                            <span className="leg-name-title">{leg.title || leg.location || 'Layover'}</span>
                                                            <span className="leg-type-tag">Layover</span>
                                                        </div>
                                                        <div className="leg-middle">
                                                            <p className="location-desc-text" dangerouslySetInnerHTML={{ __html: leg.description?.replace(/(\d+ hr \d+ min)/, '<strong>$1</strong>') }}></p>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </React.Fragment>
                                ))}

                                {card.destCommentWeb && (
                                    <div className="timeline-segment">
                                        <div className="sidebar-visual">
                                            <div className="node-icon-box">
                                                <img src="/icon3.svg" alt="Commute" style={{ width: 24, height: 24 }} />
                                            </div>
                                        </div>
                                        <div className="leg-details-container">
                                            <div className="expanded-leg-card location-type commute-type">
                                                <div className="leg-upper">
                                                    <span className="leg-name-title">{card.destCommentWebTitle || 'Reach Destination'}</span>
                                                    <span className="leg-type-tag">Commute</span>
                                                </div>
                                                <div className="leg-middle">
                                                    <p className="location-desc-text">{card.destCommentWeb}</p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </div>
            )}
            <LoadingModal
                isOpen={isBookingLoading}
                title="Processing Your Booking"
                message="We are fetching the latest fare and availability for you. This will only take a moment."
                showWarning={true}
                warningText="Please do not refresh this page or use the back button while we process your request."
            />
        </div>
    );
};

export default FlightTrainResultCard;
